import { OrderTrackingShipmentStatus } from "@redotech/order-tracking-common/order-tracking-types";
import type {
  TrackingPageOrder,
  TrackingPageShipment,
} from "@redotech/order-tracking-common/tracking-page-order-definition";
import { useRequiredContext } from "@redotech/react-util/context";
import { useTranslation } from "@redotech/redo-i18n";
import {
  getCarrierTrackingUrl,
  resolveCarrier,
} from "@redotech/redo-order-tracking-settings-common/carriers/carrier-resolver";
import type { TrackingTextRule } from "@redotech/redo-order-tracking-settings-common/tracking-text-rule";
import { memo, useMemo } from "react";
import { Flex } from "../../../flex";
import { ExternalLink } from "../../../link";
import { PickupConfig } from "../../../settings-elements/order/pick-up-config-util";
import { Text } from "../../../text";
import { TrackingPageContext } from "../../context/tracking-page-context";
import {
  getBodyStyles,
  getDeliveryDateStyles,
  getHeaderStyles,
  getLabelStyles,
  getLinkStyles,
  getTrackingNumberUrlStyles,
} from "../shared/block-styles";
import { shouldHidePosTrackingDetails } from "./tracking-details-pos-utils";
import {
  CarrierBrandIcon,
  formatDeliveryWeekdayMonth,
  formatEventMessage,
  formatEventTimestamp,
  getShipmentStatusText,
  shouldHideDeliveryEstimateSection,
  type StatusLabelOverrides,
} from "./tracking-details-shared";
import type { TrackingMapStyle } from "./tracking-map";
import { TrackingMap } from "./tracking-map";

export const TrackingDetailsDesktop = memo(function TrackingDetailsDesktop({
  shipment,
  order,
  pickupConfig,
  stalledDays,
  optionalSubtext,
  title,
  showMap,
  mapStyle,
  statusLabelOverrides,
  hideDeliveryEstimateWhenUnknown,
  textRules,
  hideTrackingNumber,
}: {
  shipment: TrackingPageShipment | undefined;
  order: TrackingPageOrder;
  pickupConfig: PickupConfig;
  stalledDays: number | null;
  optionalSubtext: string | undefined;
  title: string | undefined;
  showMap: boolean | undefined;
  mapStyle: TrackingMapStyle;
  statusLabelOverrides: StatusLabelOverrides | undefined;
  hideDeliveryEstimateWhenUnknown: boolean | undefined;
  textRules?: readonly TrackingTextRule[];
  hideTrackingNumber?: boolean;
}) {
  const { returnAppSettings, pageSettings } =
    useRequiredContext(TrackingPageContext);
  const { t, i18n } = useTranslation();

  const headerStyles = getHeaderStyles(pageSettings);
  const bodyStyles = getBodyStyles(pageSettings);
  const labelStyles = getLabelStyles(pageSettings);
  const deliveryDateStyles = getDeliveryDateStyles(pageSettings);
  const trackingUrlStyles = getTrackingNumberUrlStyles(pageSettings);
  const linkStyles = getLinkStyles(pageSettings);

  const isDelivered =
    shipment?.status === OrderTrackingShipmentStatus.DELIVERED;
  const deliveryDate = isDelivered
    ? shipment?.deliveredAt
    : shipment?.estimatedDeliveryAt;

  const eddDistrusted = useMemo(() => {
    const resolved = shipment?.carrier
      ? resolveCarrier(shipment.carrier)
      : undefined;
    return !!(resolved?.found && resolved.definition.distrustDeliveryEstimate);
  }, [shipment?.carrier]);

  const hideEstimateSection = shouldHideDeliveryEstimateSection({
    hideWhenUnknown: hideDeliveryEstimateWhenUnknown,
    date: deliveryDate,
    isDelivered,
  });

  const orderedEvents = useMemo(() => {
    const events = shipment?.trackingEvents?.length
      ? [...shipment.trackingEvents]
      : [];
    return events.sort(
      (a, b) =>
        (b.timestamp?.getTime?.() ?? 0) - (a.timestamp?.getTime?.() ?? 0),
    );
  }, [shipment?.trackingEvents]);

  const trackingCode = shipment?.trackingCode;
  const trackingUrl =
    getCarrierTrackingUrl(
      shipment?.carrier ?? undefined,
      trackingCode ?? undefined,
    ) ?? shipment?.trackingUrl;
  const showTrackingLink =
    !!trackingUrl &&
    !order.cancelledAt &&
    returnAppSettings.storeUrl !== "shopconzuri.myshopify.com";

  if (shouldHidePosTrackingDetails(order, shipment)) {
    return null;
  }

  return (
    <Flex
      dir="column"
      flex={1}
      height="full"
      overflowY="auto"
      style={{ minHeight: 0 }}
      w="full"
    >
      <Flex dir="column" gap="lg" p="3xl" shrink={0} w="full">
        {!hideEstimateSection && (
          <Flex dir="column" gap="xs">
            <Text fontSize="xl" fontWeight="bold" style={headerStyles}>
              {title ??
                (isDelivered ? t("Delivered") : t("Estimated delivery"))}
            </Text>
            {deliveryDate ? (
              <>
                <Text fontSize="md" style={bodyStyles}>
                  {formatDeliveryWeekdayMonth(deliveryDate, i18n.language)}
                </Text>
                <Text
                  fontSize="6xl"
                  fontWeight="bold"
                  style={{
                    ...headerStyles,
                    ...deliveryDateStyles,
                    lineHeight: 1,
                  }}
                >
                  {deliveryDate.getUTCDate()}
                </Text>
              </>
            ) : (
              <Text fontSize="md" style={bodyStyles}>
                {eddDistrusted ? t("Unknown ETA") : t("Awaiting ETA")}
              </Text>
            )}
          </Flex>
        )}

        {showMap && (
          <TrackingMap
            mapStyle={mapStyle}
            order={order}
            shipment={shipment}
            statusLabelOverrides={statusLabelOverrides}
          />
        )}

        <Flex dir="column" gap="xxs">
          <Text
            fontSize="xs"
            fontWeight="medium"
            style={labelStyles}
            textColor="secondary"
          >
            {t("Status")}
          </Text>
          <Text fontSize="md" fontWeight="semibold" style={bodyStyles}>
            {getShipmentStatusText({
              cancelReason: order.cancelReason,
              pickupConfig,
              shipment,
              source: order.sourceName,
              stalledDays,
              statusLabelOverrides,
            })}
          </Text>
        </Flex>

        {orderedEvents.length > 0 && (
          <Flex dir="column" gap="md">
            {orderedEvents.map((e, i) => {
              const isLast = i === orderedEvents.length - 1;
              const dotColor = bodyStyles.color ?? "currentColor";
              return (
                <Flex
                  dir="column"
                  gap="xxs"
                  key={i}
                  style={{
                    position: "relative",
                    paddingLeft: "var(--redo-spacing-3xl)",
                  }}
                >
                  <span
                    style={{
                      position: "absolute",
                      left: 0,
                      top: 0,
                      width: 16,
                      height: 16,
                      borderRadius: "var(--border-radius-3xl)",
                      border: `2px solid ${dotColor}`,
                      boxSizing: "border-box",
                    }}
                  />
                  {i !== 0 && (
                    <span
                      style={{
                        position: "absolute",
                        left: 3,
                        top: 3,
                        width: 10,
                        height: 10,
                        borderRadius: "var(--border-radius-2xl)",
                        backgroundColor: dotColor,
                      }}
                    />
                  )}
                  {!isLast && (
                    <span
                      style={{
                        position: "absolute",
                        left: 7,
                        top: 16,
                        width: 2,
                        height: "calc(100% - 8px)",
                        backgroundColor: dotColor,
                        borderRadius: "var(--border-radius-3xl)",
                      }}
                    />
                  )}
                  <Text fontSize="xs" fontWeight="semibold" style={bodyStyles}>
                    {formatEventTimestamp(e.timestamp, i18n.language)}
                  </Text>
                  <Text fontSize="xs" style={labelStyles}>
                    {formatEventMessage(e, textRules)}
                  </Text>
                </Flex>
              );
            })}
          </Flex>
        )}

        {!hideTrackingNumber && trackingCode && (
          <Flex dir="column" gap="xxs">
            <Text
              fontSize="xs"
              fontWeight="medium"
              style={labelStyles}
              textColor="secondary"
            >
              {t("Tracking")}
            </Text>
            <Flex align="center" gap="xs">
              <CarrierBrandIcon carrier={shipment?.carrier ?? null} />
              {showTrackingLink && trackingUrl ? (
                <ExternalLink
                  showIcon={false}
                  style={linkStyles}
                  url={trackingUrl}
                >
                  <Text
                    fontSize="xs"
                    fontWeight="semibold"
                    style={{
                      ...trackingUrlStyles,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                    textColor="secondary"
                  >
                    {trackingCode}
                  </Text>
                </ExternalLink>
              ) : (
                <Text
                  fontSize="xs"
                  fontWeight="semibold"
                  style={{
                    ...bodyStyles,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  textColor="secondary"
                >
                  {trackingCode}
                </Text>
              )}
            </Flex>
          </Flex>
        )}

        {optionalSubtext && (
          <Text fontSize="sm" style={labelStyles}>
            {optionalSubtext}
          </Text>
        )}
      </Flex>
    </Flex>
  );
});
